import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sessions as sessionsApi } from '../api/client';

function ChallengeStatusBadge({ session }) {
  if (!session.challenge_config) return null;
  const cs = session.challenge_status;
  const name = session.challenge_config.name;
  return (
    <span className={`challenge-session-badge challenge-session-${cs || 'active'}`}>
      {name} — {(cs || 'active').toUpperCase()}
    </span>
  );
}

function SessionCard({ s, onDelete }) {
  const isChallenge = !!s.challenge_config;
  const pnl = s.stats?.total_pnl;

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${s.name || `Session #${s.id}`}"? All trades and data will be lost.`)) return;
    onDelete(s.id);
  };

  return (
    <div className={`card dataset-card ${isChallenge ? 'challenge-card' : ''}`}>
      <h3>{s.name || `Session #${s.id}`}</h3>
      <div className="dataset-meta">
        <span className="badge">{s.instrument}</span>
        <span className="badge">{s.timeframe}</span>
        {s.status === 'in_progress' && <span>Bar {s.current_bar_index}</span>}
      </div>
      <ChallengeStatusBadge session={s} />
      <div className="dataset-dates">
        Capital: {parseFloat(s.starting_capital).toLocaleString()}
        {s.final_capital && ` → ${parseFloat(s.final_capital).toLocaleString()}`}
      </div>
      {s.stats && s.stats.total_trades > 0 && (
        <div className="session-stats-row">
          <span className="session-stat">
            <span className="session-stat-label">Win Rate</span>
            <span className="session-stat-value">{s.stats.win_rate?.toFixed(1)}%</span>
          </span>
          <span className="session-stat">
            <span className="session-stat-label">P&L</span>
            <span className={`session-stat-value ${pnl > 0 ? 'pnl-positive' : pnl < 0 ? 'pnl-negative' : ''}`}>
              {pnl >= 0 ? '+' : ''}{pnl?.toFixed(2)}
            </span>
          </span>
          <span className="session-stat">
            <span className="session-stat-label">Trades</span>
            <span className="session-stat-value">{s.stats.total_trades}</span>
          </span>
          <span className="session-stat">
            <span className="session-stat-label">Expectancy</span>
            <span className="session-stat-value">{s.stats.expectancy?.toFixed(2)}</span>
          </span>
        </div>
      )}
      <div className="dataset-actions">
        {s.status === 'in_progress' && (
          <Link to={`/replay/${s.id}`} className="btn btn-sm">Resume</Link>
        )}
        {s.status === 'completed' && (
          <span className="badge" style={{ opacity: 0.6 }}>Completed</span>
        )}
        <button className="btn btn-sm btn-danger" onClick={handleDelete} style={{ marginLeft: 'auto' }}>
          Delete
        </button>
      </div>
    </div>
  );
}

export default function SessionsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchSessions = () => {
    sessionsApi.list()
      .then((res) => setList(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSessions(); }, []);

  const handleDelete = (id) => {
    sessionsApi.delete(id).then(() => {
      setList(prev => prev.filter(s => s.id !== id));
    }).catch(() => alert('Failed to delete session.'));
  };

  if (loading) return <div className="page">Loading...</div>;

  // Apply filters
  let filtered = list;
  if (filter === 'challenge') filtered = filtered.filter(s => !!s.challenge);
  if (filter === 'free') filtered = filtered.filter(s => !s.challenge);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      s.instrument.toLowerCase().includes(q) ||
      (s.challenge_config?.name || '').toLowerCase().includes(q)
    );
  }

  const inProgress = filtered.filter((s) => s.status === 'in_progress');
  const completed = filtered.filter((s) => s.status === 'completed');
  const abandoned = filtered.filter((s) => s.status === 'abandoned');
  const challengeCount = list.filter(s => !!s.challenge).length;

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2>My Sessions</h2>
        <div className="source-tabs" style={{ margin: 0 }}>
          <button className={`source-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}>All ({list.length})</button>
          <button className={`source-tab ${filter === 'free' ? 'active' : ''}`}
            onClick={() => setFilter('free')}>Free ({list.length - challengeCount})</button>
          <button className={`source-tab ${filter === 'challenge' ? 'active' : ''}`}
            onClick={() => setFilter('challenge')}>Challenges ({challengeCount})</button>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        className="session-search"
        placeholder="Search sessions by name, instrument, challenge..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {inProgress.length > 0 && (
        <>
          <h3 style={{ margin: '1rem 0 0.5rem' }}>In Progress ({inProgress.length})</h3>
          <div className="dataset-grid">
            {inProgress.map((s) => <SessionCard key={s.id} s={s} onDelete={handleDelete} />)}
          </div>
        </>
      )}

      {completed.length > 0 && (
        <>
          <h3 style={{ margin: '1rem 0 0.5rem' }}>Completed ({completed.length})</h3>
          <div className="dataset-grid">
            {completed.map((s) => <SessionCard key={s.id} s={s} onDelete={handleDelete} />)}
          </div>
        </>
      )}

      {abandoned.length > 0 && (
        <>
          <h3 style={{ margin: '1rem 0 0.5rem', opacity: 0.5 }}>Abandoned ({abandoned.length})</h3>
          <div className="dataset-grid">
            {abandoned.map((s) => <SessionCard key={s.id} s={s} onDelete={handleDelete} />)}
          </div>
        </>
      )}

      {filtered.length === 0 && (
        <div className="empty-state">
          {search ? 'No sessions match your search.' : 'No sessions yet. Start a replay from your datasets.'}
          {!search && <Link to="/" className="btn" style={{ marginTop: '0.5rem' }}>Go to Datasets</Link>}
        </div>
      )}
    </div>
  );
}
